import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { usePageTitle } from "@/hooks/useMimis";

const Shell = ({ icon, title, text, children, testId }) => (
  <main className="grid min-h-[80vh] place-items-center px-5 pb-20 pt-32" data-testid={testId}>
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-soft w-full max-w-md p-9 text-center">
      <div className="mx-auto grid h-16 w-16 place-items-center rounded-full">{icon}</div>
      <h1 className="font-display mt-6 text-3xl font-bold tracking-tight">{title}</h1>
      <p className="mt-3 text-[#52525b]">{text}</p>
      <div className="mt-8">{children}</div>
    </motion.div>
  </main>
);

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [piano, setPiano] = useState(null);
  const tries = useRef(0);
  usePageTitle("Pagamento | MIMIS", true);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return undefined;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data?.payment_status === "paid") {
          setPiano(data?.piano ?? null);
          return setState("paid");
        }
        if (data?.status === "expired") return setState("error");
      } catch {
        /* ritenta */
      }
      tries.current += 1;
      if (tries.current >= 10) return setState("timeout");
      timer = setTimeout(poll, 2000);
      return undefined;
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  if (state === "paid")
    return (
      <Shell testId="payment-success" icon={<span className="grid h-16 w-16 place-items-center rounded-full bg-[#0066FF] text-white"><Check className="h-8 w-8" strokeWidth={3} /></span>} title="Benvenuto in MIMIS!" text={piano === "starter" ? "Il piano Starter è attivo: hai 10 video al mese. Inizia subito!" : "Il piano Pro è attivo: video illimitati. Aggiungi il nome della tua attività e le tue foto per personalizzare i video."}>
        <div className="flex flex-col items-center gap-3">
          <Link to="/tool/mimis-video" className="btn-blu h-12 px-6 text-sm" data-testid="payment-create-video-link">Crea i tuoi video <ArrowRight className="arrow h-4 w-4" /></Link>
          {piano !== "starter" && <Link to="/abbonamento#brand" className="btn-ghost h-12 px-6 text-sm" data-testid="payment-brand-link">Aggiungi il nome della tua attività</Link>}
          <Link to="/abbonamento" className="text-sm font-medium text-[#52525b] hover:text-[#111111]" data-testid="payment-account-link">Il mio abbonamento</Link>
          <Link to="/home" className="text-sm font-medium text-[#52525b] hover:text-[#111111]" data-testid="payment-home-link">Torna alla home</Link>
        </div>
      </Shell>
    );
  if (state === "checking")
    return <Shell testId="payment-checking" icon={<Loader2 className="h-10 w-10 animate-spin text-[#0066FF]" />} title="Verifica in corso…" text="Stiamo confermando il pagamento con Stripe. Ci vuole un attimo." />;
  return (
    <Shell testId="payment-error" icon={<span className="grid h-16 w-16 place-items-center rounded-full bg-[#F5F5F7]"><X className="h-8 w-8" /></span>} title="Non riusciamo a confermare" text={state === "timeout" ? "La conferma sta richiedendo più del previsto. Controlla la tua email o riprova tra qualche minuto." : "Il pagamento non risulta completato."}>
      <Link to="/tool/mimis-video#prezzo" className="btn-ghost h-12 px-6 text-sm">Torna al tool</Link>
    </Shell>
  );
};

export const PaymentCancel = () => {
  usePageTitle("Pagamento annullato | MIMIS", true);
  return (
    <Shell testId="payment-cancel" icon={<span className="grid h-16 w-16 place-items-center rounded-full bg-[#F5F5F7]"><X className="h-8 w-8" /></span>} title="Pagamento annullato" text="Nessun addebito effettuato. Quando vuoi, il tuo posto ti aspetta.">
      <Link to="/tool/mimis-video#prezzo" className="btn-blu h-12 px-6 text-sm" data-testid="payment-retry-link">Riprova <ArrowRight className="arrow h-4 w-4" /></Link>
    </Shell>
  );
};
